import FluxComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'

import { checkEditorStateParity } from './editorStateDrift'
import { isMirrorWriteInProgress, runMirrorWrite } from './mirrorGate'
import { selectEditorForDraft } from '../editorSlice'
import type { ComposerStore } from '../composerStore'

export interface FluxToEditorStateMirror {
  unsubscribe: () => void
}

/**
 * Register a Flux `ComposerStore` change listener that mirrors every draft's
 * live `BufferEditor` into Zustand's `editorSlice` — by IDENTITY, not value
 * (see `editorSlice.ts` and
 * `~/notes/notes/claude/plans/composer/editor-state-migration.md`).
 *
 * Runs synchronously on every Flux emission, unlike the omni content
 * mirror's debounced asset pass: a reference compare plus a single pointer
 * assignment is dirt cheap, so there's no cost reason to defer it, and
 * deferring an identity mirror risks a reader observing a stale pointer
 * mid-keystroke.
 *
 * Reconciles the full draft set every pass, not just changed drafts. Note
 * `FluxComposerStore.getDrafts()` is a fixed one-per-service set for the
 * whole page lifetime — deselecting a channel only toggles `isEnabled`, it
 * never removes the draft — so the removal branch below has no live trigger
 * from normal composer use today. It exists for `resetState()` (composer
 * close), which replaces Flux's whole draft set without emitting a change:
 * this Zustand store is recreated fresh per session (`initComposerStore`),
 * so a *new* session's mirror install always starts from an empty
 * `editorSlice` and reseeds correctly regardless: the branch is a defensive
 * backstop, not the primary mechanism, and asserted in
 * `installFluxToEditorStateMirror.test.ts`.
 */
export function installFluxToEditorStateMirror(
  store: ComposerStore,
): FluxToEditorStateMirror {
  const installedAt = performance.now()

  const syncEditors = (): void => {
    const drafts = FluxComposerStore.getDrafts()

    runMirrorWrite(() => {
      const state = store.getState()
      // `draft.id` is typed as a narrow per-service literal union; widen
      // explicitly since `editorSlice.editors`'s keys (and Object.keys of it)
      // are plain `string`.
      const fluxDraftIds = new Set<string>(drafts.map((draft) => draft.id))

      for (const draft of drafts) {
        if (!draft.editorState) continue
        if (selectEditorForDraft(state, draft.id) !== draft.editorState) {
          state.setEditor(draft.id, draft.editorState)
        }
      }

      for (const draftId of Object.keys(state.editors)) {
        if (!fluxDraftIds.has(draftId)) {
          state.removeEditor(draftId)
        }
      }
    })

    checkEditorStateParity(store, performance.now() - installedAt)
  }

  const handleChange = (): void => {
    if (isMirrorWriteInProgress()) return
    syncEditors()
  }

  // Seed immediately from whatever Flux state already exists (e.g. edit-mode
  // init populates drafts synchronously before this mirror installs), then
  // stay in sync on every subsequent emission.
  syncEditors()
  FluxComposerStore.addChangeListener(handleChange)

  return {
    unsubscribe: (): void => {
      FluxComposerStore.removeChangeListener(handleChange)
    },
  }
}
